import {
  PhArrowSquareOut,
  PhCopy,
  PhFile,
  PhFolder,
  PhPencilSimple,
  PhToolbox,
  PhTrash,
} from '@phosphor-icons/vue';
import i18n from '@/i18n';
import { isSynthetic, type FileSystemItem } from '../../types';
import type { FileManagerTool } from '../tools/fileManagerTool';
import {
  getItemIcon,
  getOfficeAppLabel,
} from '../../views/file-explorer-view/sub-components/render-modes/_shared/file-object/sub-components/file-item/get-file-icon';
import { deleteMenuLabel } from './useFileManagerDelete';
import {
  type ContextMenuActionItem,
  type ContextMenuGroupSection,
  type ContextMenuHeaderSection,
  type ContextMenuItem,
  type ContextMenuSection,
  type ContextMenuSubmenuItem,
} from './useFileManagerContextMenu';

const { t } = i18n.global;

// Every gated action a menu can offer. A key's mere *presence* is the visibility gate — builders
// never see a canX boolean, just whether the slot exists — and `request` is always a zero-argument
// closure the caller has already bound to whatever target/path it means (an item's own path, the
// current folder, the project root, ...). That's what lets createFolderMenuItem/createFileMenuItem
// below be reused verbatim by both the single-folder item menu and the background menu: neither
// needs to know what it's creating inside, only whether the caller offered the slot at all.
export type MenuActions = {
  createFolder?: { request: () => void };
  createFile?: { request: () => void };
  rename?: { request: () => void };
  copy?: { request: () => void };
  delete?: { request: () => void };
  /**
   * The "Verktyg ›" submenu's contents, already filtered by the caller (useFileManagerTools) for both
   * structural fitness and permission.
   *
   * Shaped as an array rather than a `{ request }` slot because a tool's identity survives into the
   * menu — each becomes its own child row. Same "presence is the gate" rule as every key above,
   * extended one step: an EMPTY list hides the submenu too. That is what lets every call site pass this
   * unconditionally and still get nothing on folders, synthetic rows or mixed selections, without a
   * single `if` about folders anywhere in the menu layer.
   */
  tools?: { items: FileManagerTool[] };
};

export type ContextMenuKind =
  | 'single-file'
  | 'single-folder'
  | 'synthetic'
  | 'multi-file'
  | 'multi-folder'
  | 'mixed';

// Pure: classifies a resolved target set so the context-menu builder dispatch below can pick the
// right action list without any kind knowing about the others.
//
// 'synthetic' wins over every other kind as soon as ANY target is one: a synthetic row (a SharePoint
// document library, or an implied namespace level above one) is not a driveItem, so it cannot be
// renamed or deleted through this API at all (that needs ManageLists — D7/D9; a namespace has no
// backing object whatsoever). Checking "any" rather than "all" covers a multi-selection above the
// mounts, where every row is synthetic and the multi builders would otherwise happily offer Delete.
export const classifyContextMenuTarget = (
  target: FileSystemItem[],
): ContextMenuKind => {
  if (target.some(isSynthetic)) return 'synthetic';
  const files = target.filter((i) => i.type === 'file').length;
  const folders = target.length - files;
  if (target.length === 1) return files ? 'single-file' : 'single-folder';
  if (files && folders) return 'mixed';
  return files ? 'multi-file' : 'multi-folder';
};

const group = (items: ContextMenuItem[]): ContextMenuGroupSection => ({ kind: 'group', items });

// Only single-file/single-folder builders call this — target.length === 1 is guaranteed by
// classifyContextMenuTarget for those kinds, so target[0] always names the item the menu is for.
const headerSection = (item: FileSystemItem): ContextMenuHeaderSection => {
  const { icon, color } = getItemIcon(item);
  return { kind: 'header', icon, color, name: item.name };
};

/**
 * Copies a name to the clipboard.
 *
 * This used to be a "Kopiera namn" menu ITEM. It is now what clicking the menu's own header does —
 * the header already shows the name, so the name itself became the affordance and the redundant row
 * went away. Kept here rather than inlined into the header component so the silent-by-design
 * reasoning below stays with the behaviour it describes.
 *
 * Silent by design — no success/failure toast, even though notificationService exists and is used
 * elsewhere in the app for this kind of feedback. The rejection is still caught so a clipboard
 * failure (no permission, insecure context) never surfaces as an unhandled-rejection warning.
 */
export const copyNameToClipboard = (name: string): void => {
  navigator.clipboard.writeText(name).catch(() => {});
};

// Every factory below returns a `kind: 'action'` item — an ordinary clickable row. `ContextMenuItem`
// is a union now (see useFileManagerContextMenu.ts), the other member being the `kind: 'submenu'`
// item that toolsMenuItem builds, so the discriminant is explicit on every literal rather than
// implied by shape.
//
// Danger-styled, hide-on-missing (same convention as "Open in X" below hiding on a missing webUrl):
// a builder that omits the Delete entry entirely — not a disabled one — when the caller doesn't
// offer the `delete` action slot. Still takes `target` (unlike the other atomic factories below)
// because its label is count-aware (deleteMenuLabel).
const deleteMenuItem = (
  target: FileSystemItem[],
  request: () => void,
): ContextMenuActionItem => ({
  kind: 'action',
  label: deleteMenuLabel(target),
  icon: PhTrash,
  danger: true,
  action: request,
});

// Single-item only by construction — Rename only ever appears in the single-file/single-folder
// builders below, never the multi-selection ones. `request` is already bound to the right item by
// the caller, so this needs nothing else.
const renameMenuItem = (request: () => void): ContextMenuActionItem => ({
  kind: 'action',
  label: t('fileManager.core.contextMenuBuilders.rename'),
  icon: PhPencilSimple,
  action: request,
});

// Duplicates the item into its own folder under a name the user is then asked for. Same
// single-item-only, pre-bound-request convention as renameMenuItem above.
//
// Offered for files AND folders: nothing about the copy path is file-specific — Graph's copy action
// is recursive, useCopyItemMutation already invalidates a directory's grandparent for the
// hasFolderChildren flip, and nextCopyBaseName treats a folder as a name with no extension. A folder
// copy can outrun the 5-minute monitor deadline, which is not a failure and is already reported as
// such ("fortsätter i SharePoint"), so it needs nothing extra here.
//
// Still absent from buildSyntheticMenu (a library/namespace is not a driveItem) and from
// buildMultiMenu (one typed name cannot serve N items).
const copyMenuItem = (request: () => void): ContextMenuActionItem => ({
  kind: 'action',
  label: t('fileManager.core.contextMenuBuilders.copy'),
  icon: PhCopy,
  action: request,
});

// Folder-only by construction (never appears in buildSingleFileMenu — a file can't have children).
// `request` is already bound to the right parent path by the caller — this is what lets the exact
// same factory serve both the single-folder item menu (parent = that folder) and the background
// menu (parent = currentPath, or the project root for the tree) with no branching here.
const createFolderMenuItem = (request: () => void): ContextMenuActionItem => ({
  kind: 'action',
  label: t('fileManager.core.contextMenuBuilders.createFolder'),
  icon: PhFolder,
  action: request,
});

// Same folder-only, hide-on-missing, pre-bound-request convention as createFolderMenuItem above.
const createFileMenuItem = (request: () => void): ContextMenuActionItem => ({
  kind: 'action',
  label: t('fileManager.core.contextMenuBuilders.createFile'),
  icon: PhFile,
  action: request,
});

// The one submenu-shaped item in this file. Each tool becomes a child row bound to THIS menu's target,
// so a tool never has to ask what was right-clicked.
//
// Registry order is menu order — no sorting, no priority field. Two tools do not pay for one.
const toolsMenuItem = (
  tools: FileManagerTool[],
  target: FileSystemItem[],
): ContextMenuSubmenuItem => ({
  kind: 'submenu',
  label: t('fileManager.core.contextMenuBuilders.tools'),
  icon: PhToolbox,
  items: tools.map((tool) => ({
    kind: 'action',
    label: tool.label,
    icon: tool.icon,
    action: () => tool.run(target),
  })),
});

// Zero or one section, so a builder can spread it unconditionally. `.length` and not just the key's
// presence: a caller that hands over an empty array must get no group and therefore no orphan divider,
// exactly as if it had omitted the slot.
const toolsSections = (
  target: FileSystemItem[],
  actions: MenuActions,
): ContextMenuSection[] =>
  actions.tools?.items.length
    ? [group([toolsMenuItem(actions.tools.items, target)])]
    : [];

// File-only by design — folders never get an "Open in SharePoint" action (context menu or the
// details side panel). Also single-item-only (never one tab per selected item) and hides itself
// entirely when the item has no webUrl, rather than showing a disabled/dead entry. Label names the
// actual Office/PDF app SharePoint opens the file in (see get-file-icon.ts's getOfficeAppLabel),
// falling back to the generic "Open in SharePoint" for unrecognized extensions.
//
// A file with no webUrl still gets a menu, just without Open.
//
// The actions are deliberately split into separate groups: Skapa kopia/Byt namn is a lightweight
// metadata-ish group, "Open in X" is the primary navigation action — same logical split file
// managers conventionally draw between item-editing actions and open/navigate actions. Verktyg is a
// third category again (act on the selection with something outside this file manager), so it gets its
// own group immediately before Delete, which stays last as the danger anchor. Each group
// section renders with a divider before it automatically (ContextMenu.vue), so an absent group
// (e.g. no webUrl, or a tier offering neither copy nor rename) means no orphan divider either.
//
// With no action offered at all this returns just the header, which is still a real menu: clicking
// the header copies the item's name (see copyNameToClipboard). openContextMenuIfAny knows that.
export const buildSingleFileMenu = (
  target: FileSystemItem[],
  actions: MenuActions,
): ContextMenuSection[] => {
  const webUrl = target[0].webUrl;
  const sections: ContextMenuSection[] = [headerSection(target[0])];

  // Guarded, unlike before: this group used to always contain "Kopiera namn", so it could never be
  // empty. Now that copying the name is the header's job, a file whose tier offers neither copy nor
  // rename would otherwise push an empty group and render a stray divider.
  const metaItems: ContextMenuItem[] = [];
  if (actions.copy) metaItems.push(copyMenuItem(actions.copy.request));
  if (actions.rename) metaItems.push(renameMenuItem(actions.rename.request));
  if (metaItems.length) sections.push(group(metaItems));

  if (webUrl) {
    sections.push(
      group([
        {
          kind: 'action',
          label: getOfficeAppLabel(target[0].name),
          icon: PhArrowSquareOut,
          action: () => window.open(webUrl, '_blank'),
        },
      ]),
    );
  }
  sections.push(...toolsSections(target, actions));
  if (actions.delete) sections.push(group([deleteMenuItem(target, actions.delete.request)]));
  return sections;
};

export const buildSingleFolderMenu = (
  target: FileSystemItem[],
  actions: MenuActions,
): ContextMenuSection[] => {
  // Create folder/file lead as their own group — the primary "act on this container" actions —
  // separated from the Skapa kopia/Byt namn "metadata-ish" group below.
  const sections: ContextMenuSection[] = [headerSection(target[0])];

  const createItems: ContextMenuItem[] = [];
  if (actions.createFolder) createItems.push(createFolderMenuItem(actions.createFolder.request));
  if (actions.createFile) createItems.push(createFileMenuItem(actions.createFile.request));
  if (createItems.length) sections.push(group(createItems));

  const metaItems: ContextMenuItem[] = [];
  if (actions.copy) metaItems.push(copyMenuItem(actions.copy.request));
  if (actions.rename) metaItems.push(renameMenuItem(actions.rename.request));
  if (metaItems.length) sections.push(group(metaItems));

  // Passed unconditionally here even though no tool is folder-shaped today: isAvailable decides, and a
  // folder-only tool later needs no edit to this builder. Same reason there is no `if` about item type
  // anywhere in this file.
  sections.push(...toolsSections(target, actions));

  if (actions.delete) sections.push(group([deleteMenuItem(target, actions.delete.request)]));
  return sections;
};

// A row that is not a driveItem: a document library's own root, or an implied namespace level above
// one. Structurally the single-folder menu minus rename and delete, since neither operation exists for
// either (renaming/deleting a library needs ManageLists, which D7 deliberately withholds from everyone
// but the service account; a namespace has no backing object at all). Creating inside a library is
// ordinary, so Create folder/Create file are offered exactly as for any folder — and for a namespace
// the caller's own path-shaped gate (canCreateFolderAt) withholds them, since there is no drive there.
//
// Also the one builder that deliberately ignores `tools`. Every tool acts on driveItems — bytes to read,
// an id to address — and a library root or namespace has neither, so offering Verktyg here could only
// produce a tool that fails the moment it runs. Unlike the folder builder above, this is not a case
// isAvailable could be trusted to decide: it would be one more thing every future tool had to remember
// to exclude.
//
// A multi-target selection gets no menu at all rather than a create menu bound to target[0]: above the
// mounts every row is synthetic, and "create a folder in whichever of these I happened to right-click"
// is not a thing a user asked for. Returning [] falls through to the native menu, the same convention
// buildMultiMenu already uses when nothing is offered.
export const buildSyntheticMenu = (
  target: FileSystemItem[],
  actions: MenuActions,
): ContextMenuSection[] => {
  if (target.length !== 1) return [];

  const sections: ContextMenuSection[] = [headerSection(target[0])];

  const createItems: ContextMenuItem[] = [];
  if (actions.createFolder) createItems.push(createFolderMenuItem(actions.createFolder.request));
  if (actions.createFile) createItems.push(createFileMenuItem(actions.createFile.request));
  if (createItems.length) sections.push(group(createItems));

  return sections;
};

// Shared by multi-file/multi-folder/mixed: there is no canonical single item for a header — and so,
// since copying the name is now the header's job, no way to copy a name here either (it would be
// meaningless for a multi-selection anyway).
//
// Delete used to be the only thing these three kinds could offer, so "no delete" was the same as "no
// menu". Verktyg breaks that: a multi-FILE selection is exactly what the mail tool is for, and a
// read-only internal user has the tool permission without files:delete — so gating the early return on
// delete alone would leave them with no menu at all on the one selection shape the feature targets.
// multi-folder and mixed still fall through to delete-only, because isAvailable requires every target
// to be a file.
export const buildMultiMenu = (
  target: FileSystemItem[],
  actions: MenuActions,
): ContextMenuSection[] => {
  const sections: ContextMenuSection[] = toolsSections(target, actions);
  if (actions.delete) sections.push(group([deleteMenuItem(target, actions.delete.request)]));
  return sections;
};

// Kind -> builder dispatch: adding a new case later means adding/editing one builder here, kinds
// never know about each other. Every builder takes the same (target, actions) shape regardless of
// kind — buildMultiMenu simply ignores the keys it doesn't use (createFolder/createFile/rename/copy),
// since those are single-item/single-folder-only by construction, and buildSyntheticMenu ignores
// rename/delete because a synthetic row cannot have either, and `copy`/`tools` for the same reason.
// When a kind has no actions offered, the result has no group sections, which means no custom menu
// opens at all — falls back to the native browser context menu.
export const contextMenuBuilders: Record<
  ContextMenuKind,
  (target: FileSystemItem[], actions: MenuActions) => ContextMenuSection[]
> = {
  'single-file': buildSingleFileMenu,
  'single-folder': buildSingleFolderMenu,
  synthetic: buildSyntheticMenu,
  'multi-file': buildMultiMenu,
  'multi-folder': buildMultiMenu,
  mixed: buildMultiMenu,
};

// The background menu (right-click empty space, no target item at all) reuses the exact same
// createFolderMenuItem/createFileMenuItem factories the single-folder item menu uses — both callers
// pre-bind `request` to whatever parent path they mean, so nothing here needs to branch on where
// the click actually happened. No canonical single item, so unlike the single-item builders above,
// the header (if any) isn't resolved here — both call sites (FileExplorerView, DirectoryListView)
// know what "where this lands" means for them and pass it in fully-formed.
export const buildBackgroundMenu = (
  actions: Pick<MenuActions, 'createFolder' | 'createFile'>,
  header?: ContextMenuHeaderSection,
): ContextMenuSection[] => {
  const items: ContextMenuItem[] = [];
  if (actions.createFolder) items.push(createFolderMenuItem(actions.createFolder.request));
  if (actions.createFile) items.push(createFileMenuItem(actions.createFile.request));
  const sections: ContextMenuSection[] = header ? [header] : [];
  if (items.length) sections.push(group(items));
  return sections;
};
